import { useState } from 'react';
import { toast } from 'sonner';
import enpLogo from '@/assets/logos/enp.png';
import { TimelineReportData } from './useTimelineReportData';
import { format } from 'date-fns';
import { printInNewWindow } from '@/lib/printInNewWindow';

import { computeMilestoneStepperLayout, getMilestoneDisplayNote } from '@/utils/milestoneSummaryLayout';
import { SummaryMilestone } from '@/components/timeline-report/MilestoneSummarySelector';
import { hexToRgba, HIGHLIGHT_BAR_STYLE_DEFAULTS, type HighlightBarStyle } from '@/utils/highlightStyle';
import { computeDependencyEdges } from '@/utils/timelineDependencies';

// Mutable stats shared between page renders + header (for PART C debug line)
interface ExportStats { edgesDrawn: number }
interface TimelineReportExportData extends TimelineReportData {
  projectName: string;
  projectImageUrl?: string;
  milestoneTypes?: Array<{
    name?: string;
    display_name?: string;
    color?: string;
    description?: string | null;
    is_active?: boolean;
    sort_order?: number;
    created_at?: string;
    updated_at?: string;
  }>;
  teamMembers?: Array<{
    id: string;
    full_name: string;
    avatar_url?: string | null;
  }>;
  settings: {
    pageFormat: 'portrait' | 'landscape';
    showCategoryColors: boolean;
    showCategories: boolean;
    title: string;
    titlePrefix?: string;
    showLogo?: boolean;
    logoType?: 'solid' | 'enp';
    showMilestones?: boolean;
    milestoneFilters?: {
      activeMilestoneTypes?: string[];
      milestoneName?: string;
    };
    showStatistics?: boolean;
    highlightStyle?: HighlightBarStyle;
  };
  isMultiProject?: boolean;
  summaryMilestones?: SummaryMilestone[];
  showAssessmentIssues?: boolean;
  showProjectHealth?: boolean;
  showCategoryPerformance?: boolean;
  projectHealth?: {
    overallCompletion: number;
    expectedCompletion: number;
    completionGap: number;
    scheduleStatus: string;
    riskLevel: string;
    onTrackCount: number;
    atRiskCount: number;
    delayedCount: number;
    resourceStatus: {
      specsComplete: number;
      shopComplete: number;
      poComplete: number;
      totalItems: number;
    };
  };
  categoryPerformance?: Array<{
    category: string;
    totalItems: number;
    completionPercentage: number;
    expectedCompletion: number;
    completionGap: number;
    onTrackCount: number;
    atRiskCount: number;
    delayedCount: number;
    averageDuration: number;
    scheduleStatus: string;
  }>;
  issues?: Array<{
    severity: 'critical' | 'warning' | 'info';
    message: string;
    recommendation?: string;
  }>;
}

export const useTimelineReportExport = () => {
  const [isExporting, setIsExporting] = useState(false);

  // Helper function for consistent local date formatting (prevents timezone offset issues)
  const ymdLocal = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  };

  // Helper: Get initials from full name
  const getInitials = (fullName: string): string => {
    if (!fullName) return '?';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Helper: Render STT (hierarchical index) cell — fixed 28px column, sits BEFORE the avatar cell
  const renderIndexCellHTML = (label: string): string => {
    const safe = String(label ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<div style="width:28px;min-width:28px;max-width:28px;height:100%;background-color:transparent;border-right:1px solid #e5e7eb;display:flex;align-items:center;justify-content:center;text-align:center;font-size:8px;font-weight:600;color:#6b7280;">${safe}</div>`;
  };


  // Helper: Generate avatar cell HTML (24px column)
  const getAvatarCellHTML = (
    assignedUserId: string | null | undefined, 
    teamMembers: TimelineReportExportData['teamMembers'],
    isMilestone = false
  ): string => {
    const baseStyle = `
      width: 24px; min-width: 24px; max-width: 24px;
      display: flex; align-items: center; justify-content: center;
      border-right: 1px solid #e5e7eb;
      ${isMilestone ? 'background-color: transparent;' : ''}
    `;
    
    if (!assignedUserId || !teamMembers?.length) {
      return `<div style="${baseStyle}">&nbsp;</div>`;
    }
    
    const member = teamMembers.find(m => m.id === assignedUserId);
    if (!member) {
      return `<div style="${baseStyle}">&nbsp;</div>`;
    }
    
    const initials = getInitials(member.full_name);
    const initialsStyle = `
      width: 16px; height: 16px; border-radius: 50%;
      background-color: hsl(222.2 47.4% 11.2% / 0.1);
      color: hsl(222.2 47.4% 11.2%);
      font-size: 7px; font-weight: 600;
      display: flex; align-items: center; justify-content: center;
      border: 2px solid white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    `;
    
    if (member.avatar_url) {
      return `
        <div style="${baseStyle}" title="${member.full_name}">
          <img 
            src="${member.avatar_url}" 
            style="width: 16px; height: 16px; border-radius: 50%; object-fit: cover; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3);"
            onerror="this.style.display='none'; this.nextSibling.style.display='flex';"
          />
          <span style="${initialsStyle} display: none;">${initials}</span>
        </div>
      `;
    }
    
    return `
      <div style="${baseStyle}" title="${member.full_name}">
        <span style="${initialsStyle}">${initials}</span>
      </div>
    `;
  };

  // Helper to get SVG icon based on milestone status
  const getMilestoneStatusIconSVG = (status: string, color: string): string => {
    const size = 12;
    
    switch (status) {
      case 'completed':
        // Filled green circle with white checkmark and white border
        return `
          <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="11" stroke="white" stroke-width="2"/>
            <circle cx="12" cy="12" r="10" fill="#22c55e"/>
            <path d="M9 12l2 2 4-4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      case 'in_progress':
        // Filled blue circle with white border
        return `
          <svg width="${size}" height="${size}" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="11" stroke="white" stroke-width="2"/>
            <circle cx="12" cy="12" r="10" fill="#3b82f6"/>
          </svg>
        `;
      case 'blocked':
        // Filled red circle with white X and white border
        return `
          <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="11" stroke="white" stroke-width="2"/>
            <circle cx="12" cy="12" r="10" fill="#ef4444"/>
            <path d="M15 9l-6 6M9 9l6 6" stroke="white" stroke-width="2" stroke-linecap="round"/>
          </svg>
        `;
      case 'pending':
        // Filled yellow circle with white exclamation and white border
        return `
          <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="11" stroke="white" stroke-width="2"/>
            <circle cx="12" cy="12" r="10" fill="#eab308"/>
            <line x1="12" y1="8" x2="12" y2="13" stroke="white" stroke-width="2" stroke-linecap="round"/>
            <circle cx="12" cy="16" r="1" fill="white"/>
          </svg>
        `;
      default:
        // Filled gray circle (solid)
        return `
          <svg width="${size}" height="${size}" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill="#94a3b8"/>
          </svg>
        `;
    }
  };

  // Helper to get inline SVG icon for milestone status (for text display)
  const getMilestoneStatusIconInline = (status: string): string => {
    const size = 12;
    const strokeWidth = 2;
    
    switch (status) {
      case 'completed':
        // CheckCircle2 - green
        return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>`;
      case 'in_progress':
        // Circle - blue
        return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
          <circle cx="12" cy="12" r="10"></circle>
        </svg>`;
      case 'blocked':
        // XCircle - red
        return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>`;
      case 'pending':
        // AlertCircle - yellow
        return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="#eab308" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>`;
      default:
        // Circle - muted gray
        return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
          <circle cx="12" cy="12" r="10"></circle>
        </svg>`;
    }
  };

  const exportTimelineReport = async (data: TimelineReportExportData) => {
    if (isExporting) return;
    
    setIsExporting(true);
    
    try {
      
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${data.projectName.replace(/[^a-zA-Z0-9]/g, '_')}_Timeline_${timestamp}`;
      
      // Generate HTML content and add avoid-break classes
      let htmlContent = generateSimpleTimelineHTML(data);
      htmlContent = htmlContent.replace(
        /class="timeline-item/g,
        'class="timeline-item avoid-break'
      );
      
      await printInNewWindow({
        title: filename,
        htmlBody: htmlContent,
        headExtra: `
          <style>
            /* Color utilities for timeline */
            .bg-primary\\/10 { background-color: rgba(34, 34, 34, 0.1); }
            .bg-muted\\/80 { 
              background-color: hsl(210 40% 96.1% / 0.8);
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            .border-primary { border-color: hsl(222.2 47.4% 11.2%); }
            .text-primary { color: hsl(222.2 47.4% 11.2%); }
            .text-muted-foreground { color: hsl(215.4 16.3% 46.9%); }
            
            /* Timeline-specific styles */
            .timeline-bar {
              height: 32px;
              border-radius: 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 10px;
              font-weight: 600;
              color: white;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            
            /* Assessment section table styles */
            .assessment-section table {
              border-collapse: collapse;
              width: 100%;
              max-width: 754px;
              margin: 0 auto;
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            
            .assessment-section th {
              background-color: #f9fafb;
              font-weight: 600;
              text-align: left;
            }
            
            .assessment-section td,
            .assessment-section th {
              border: 1px solid #e5e7eb;
              padding: 10px;
              font-size: 12px;
            }
            
            @media print {
              .timeline-bar {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .assessment-section table {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .assessment-section th {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        `,
        pageSize: 'A4',
        pageMargin: '15mm',
        orientation: data.settings?.pageFormat || 'portrait',
      });

      toast.success(`Đã xuất báo cáo timeline thành công!`, {
        description: `${data.statistics.totalItems} items, ${data.pages.length} trang`
      });

    } catch (error) {
      console.error('[Timeline Export] Error:', error);
      toast.error('Lỗi khi xuất báo cáo timeline');
    } finally {
      setIsExporting(false);
    }
  };

  // Build complete HTML document for timeline
  const buildTimelineHtmlDocument = (data: TimelineReportExportData) => {
    const htmlContent = generateSimpleTimelineHTML(data);
    
    return `
      <!DOCTYPE html>
      <html lang="vi">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${data.settings.title} - ${data.projectName}</title>
        <link rel="icon" href="https://storage.googleapis.com/gpt-engineer-file-uploads/5Ahiblr9aqebkhCMcYkI21PicVv2/social-images/social-1758241299872-solid logo.png" type="image/png">
        <style>
          /* Import Tailwind CSS for consistency */
          @import url('https://cdn.jsdelivr.net/npm/tailwindcss@3.3.0/src/css/preflight.css');
          
          /* Color variables matching the design system */
          :root {
            --background: 0 0% 100%;
            --foreground: 222.2 84% 4.9%;
            --muted: 210 40% 96.1%;
            --muted-foreground: 215.4 16.3% 46.9%;
            --primary: 222.2 47.4% 11.2%;
            --border: 214.3 31.8% 91.4%;
          }
          
          .bg-primary\\/10 { 
            background-color: hsl(var(--primary) / 0.1); 
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          .bg-muted\\/70 { 
            background-color: hsl(var(--muted) / 0.7); 
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .bg-muted\\/80 { 
            background-color: hsl(var(--muted) / 0.8); 
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .border-primary { border-color: hsl(var(--primary)); }
          
          body {
            font-family: system-ui, -apple-system, sans-serif;
            background: hsl(var(--background));
            color: hsl(var(--foreground));
            line-height: 1.5;
            font-size: 12px;
          }
          
          /* TEMP: Force all timeline rows to fixed 24px on screen to match baked SVG Y (ROW_H=24) */
          .timeline-rows > .flex.border-b {
            height: 24px !important;
            min-height: 24px !important;
            max-height: 24px !important;
            overflow: hidden !important;
          }
          
          /* Print-specific styles */
          @media print {
            @page {
              size: ${data.settings.pageFormat === 'landscape' ? 'A4 landscape' : 'A4 portrait'};
              margin: 3mm;
            }
            
            /* Force all background colors to print */
            * {
              print-color-adjust: exact !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Generic helper for manual page breaks (kept for compatibility) */
            .print-page-break {
              /* No default page-break rule here to avoid double breaks with chunks */
            }
            
            
            .timeline-header {
              position: sticky !important;
              top: 0 !important;
              background: white !important;
              z-index: 10;
            }
            
            .print-timeline-bar {
              print-color-adjust: exact !important;
              -webkit-print-color-adjust: exact !important;
            }
            
            /* Control how timeline chunks break across pages */
            .timeline-chunk {
              /* Allow natural breaking inside; explicit page breaks controlled via :not(:last-child) */
            }
            
            .timeline-chunk:not(:last-child) {
              page-break-after: always;
              break-after: page;
            }
            
            /* Each row should avoid breaking in the middle and have consistent height */
            .timeline-rows > .flex.border-b {
              page-break-inside: avoid;
              break-inside: avoid;
              height: 24px;
              max-height: 24px;
              overflow: hidden;
            }
            
            
            .report-header-section {
              margin-bottom: 20px;
            }
            
            .bg-muted\\/30, .bg-muted\\/50, .bg-primary\\/10 {
              print-color-adjust: exact !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .preview-actions { display: none; }
            body { background: white; padding: 0; }
          }
          
          /* Layout styles */
          .w-\\[145px\\] { width: 145px; }
          .w-\\[180px\\] { width: 180px; }
          .w-\\[80px\\] { width: 80px; }
          .w-\\[225px\\] { width: 225px; }
          .w-\\[260px\\] { width: 260px; }
          .w-\\[100px\\] { width: 100px; }
          .w-\\[120px\\] { width: 120px; }
          .w-\\[35px\\] { width: 35px; }
          .w-\\[55px\\] { width: 55px; }
          .min-w-\\[55px\\] { min-width: 55px; }
          .w-2 { width: 0.5rem; }
          .w-4 { width: 1rem; }
          .h-2 { height: 0.5rem; }
          .h-4 { height: 1rem; }
          
          .p-1 { padding: 0.25rem; }
          .p-2 { padding: 0.5rem; }
          .p-3 { padding: 0.75rem; }
          .p-4 { padding: 1rem; }
          .p-6 { padding: 1.5rem; }
          
          .mb-2 { margin-bottom: 0.5rem; }
          .mb-3 { margin-bottom: 0.75rem; }
          .mb-4 { margin-bottom: 1rem; }
          .mb-6 { margin-bottom: 1.5rem; }
          .mb-8 { margin-bottom: 2rem; }
          .mt-1 { margin-top: 0.25rem; }
          .mt-6 { margin-top: 1.5rem; }
          
          .text-xs { font-size: 0.75rem; }
          .text-sm { font-size: 0.875rem; }
          .text-lg { font-size: 1.125rem; }
          .text-xl { font-size: 1.25rem; }
          .text-2xl { font-size: 1.5rem; }
          .text-\\[4px\\] { font-size: 4px; }
          .text-\\[5px\\] { font-size: 5px; }
          .text-\\[6px\\] { font-size: 6px; }
          .text-\\[7px\\] { font-size: 7px; }
          .text-\\[8px\\] { font-size: 8px; }
          .text-\\[10px\\] { font-size: 10px; }
          
          .font-medium { font-weight: 500; }
          .font-bold { font-weight: 700; }
          .leading-none { line-height: 1; }
          
          .flex { display: flex; }
          .grid { display: grid; }
          .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .gap-1 { gap: 0.25rem; }
          .gap-3 { gap: 0.75rem; }
          .gap-4 { gap: 1rem; }
          .gap-6 { gap: 1.5rem; }
          
          .items-center { align-items: center; }
          .justify-between { justify-content: space-between; }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          
          .border-b { border-bottom: 1px solid hsl(var(--border)); }
          .border-r { border-right: 1px solid hsl(var(--border)); }
          .border-t { border-top: 1px solid hsl(var(--border)); }
          .border-l-2 { border-left: 2px solid hsl(var(--primary)); }
          
          /* Vertical grid lines for timeline */
          .timeline-grid-line { border-right: 1px solid rgba(0, 0, 0, 0.08); }
          .timeline-weekend-line { border-right: 1px solid rgba(0, 0, 0, 0.12); }
          .timeline-week-line { border-right: 1px dashed rgba(0, 0, 0, 0.15); }
          .timeline-month-line { border-right: 2px solid rgba(0, 0, 0, 0.25); }
          .timeline-month-line-header { border-right: 1px dashed rgba(0, 0, 0, 0.15); }
          
          .bg-muted\\/30 { 
            background-color: hsl(var(--muted) / 0.3); 
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          .bg-muted\\/50 { 
            background-color: hsl(var(--muted) / 0.5); 
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          .bg-muted\\/70 { 
            background-color: hsl(var(--muted) / 0.7); 
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          /* Milestone-specific styles */
          .milestone-marker {
            width: 6px;
            height: 6px;
            border-radius: 1px;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .milestone-row {
            background-color: hsl(var(--muted) / 0.3);
            display: flex !important;
            opacity: 1 !important;
            visibility: visible !important;
            page-break-inside: avoid !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          @media print {
            .milestone-marker,
            .milestone-row {
              display: flex !important;
              opacity: 1 !important;
              visibility: visible !important;
              print-color-adjust: exact !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
          
          .text-foreground { color: hsl(var(--foreground)); }
          .text-muted-foreground { color: hsl(var(--muted-foreground)); }
          
          .rounded { border-radius: 0.25rem; }
          .rounded-full { border-radius: 9999px; }
          
          .truncate {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          
          .space-y-2 > * + * { margin-top: 0.5rem; }
          .space-y-3 > * + * { margin-top: 0.75rem; }
          .space-y-4 > * + * { margin-top: 1rem; }
          
          .flex-shrink-0 { flex-shrink: 0; }
          .flex-1 { flex: 1 1 0%; }
          
          .overflow-hidden { overflow: hidden; }
          .overflow-x-auto { overflow-x: auto; }
          
           .relative { position: relative; }
           .absolute { position: absolute; }
           .inset-0 { top: 0; right: 0; bottom: 0; left: 0; }
           .sticky { position: sticky; }
           .top-0 { top: 0; }
          
          .whitespace-nowrap { white-space: nowrap; }
          .origin-center { transform-origin: center; }
          .transform { transform: translateZ(0); }
          .-rotate-90 { transform: rotate(-90deg); }
          
          .hover\\:bg-muted\\/20:hover {
            background-color: hsl(var(--muted) / 0.2);
          }
          
          /* Timeline specific styles */
          .timeline-page {
            min-height: ${data.settings.pageFormat === 'landscape' ? '210mm' : '297mm'};
            width: ${data.settings.pageFormat === 'landscape' ? '297mm' : '210mm'};
          }
          
          .print-timeline-bar {
            height: 8px;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            overflow: hidden;
          }
          
          /* Preview actions */
          .preview-actions { 
            position: fixed; 
            top: 20px; 
            right: 20px; 
            z-index: 1000; 
            background: white; 
            padding: 10px; 
            border-radius: 8px; 
            box-shadow: 0 2px 10px rgba(0,0,0,0.1); 
          }
          .preview-btn { 
            margin: 0 5px; 
            padding: 8px 16px; 
            border: 1px solid #ddd; 
            background: white; 
            border-radius: 4px; 
            cursor: pointer; 
          }
          .preview-btn:hover { background: #f0f0f0; }
          .preview-btn.primary { background: #3b82f6; color: white; border-color: #3b82f6; }
        </style>
      </head>
      <body>
        <div class="preview-actions">
          <button class="preview-btn primary" onclick="window.print()">In ngay</button>
          <button class="preview-btn" onclick="window.close()">Đóng</button>
        </div>
        ${htmlContent}
      </body>
      </html>
    `;
  };

  // Generate assessment HTML for project health analysis
  const generateAssessmentHTML = (data: TimelineReportExportData) => {
    if (!data.projectHealth) return '';
    
    const { projectHealth, categoryPerformance, issues } = data;
    
    // Check if any section should be shown
    const showProjectHealth = data.showProjectHealth !== false;
    const showCategoryPerformance = data.showCategoryPerformance !== false;
    const showIssues = data.showAssessmentIssues !== false;
    
    if (!showProjectHealth && !showCategoryPerformance && !showIssues) {
      return ''; // Don't render assessment section if all are disabled
    }
    
    const getStatusBadge = (status: string) => {
      const styles = {
        'on-track': 'background-color: #22c55e; color: white;',
        'at-risk': 'background-color: #f59e0b; color: white;',
        'delayed': 'background-color: #ef4444; color: white;'
      };
      const labels = {
        'on-track': '✓ On Track',
        'at-risk': '⚠ At Risk',
        'delayed': '⚠ Delayed'
      };
      return `<span style="display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 11px; font-weight: 600; ${styles[status as keyof typeof styles] || styles['on-track']}">${labels[status as keyof typeof labels] || status}</span>`;
    };
    
    const getRiskBadge = (risk: string) => {
      const styles = {
        'low': 'background-color: #22c55e; color: white;',
        'medium': 'background-color: #f59e0b; color: white;',
        'high': 'background-color: #ef4444; color: white;'
      };
      const labels = { 'low': 'Low', 'medium': 'Medium', 'high': 'High' };
      return `<span style="display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 11px; font-weight: 600; ${styles[risk as keyof typeof styles] || styles['low']}">${labels[risk as keyof typeof labels] || risk}</span>`;
    };
    
    return `
      <div class="assessment-section" style="margin: 0 0 24px 0;">
        ${showProjectHealth ? `
        <!-- Project Health Overview -->
        <div style="background: hsl(var(--muted) / 0.3); padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="font-size: 16px; font-weight: 600; margin-bottom: 16px; color: hsl(var(--foreground));">📊 Project Health Assessment</h3>
          <table style="width: 100%; border-collapse: collapse; margin: 0 auto;">
            <tbody>
              <!-- Expected vs Actual Completion Row -->
              <tr style="border: 1px solid #e5e7eb; background: ${projectHealth.completionGap >= 0 ? '#f0fdf4' : '#fef2f2'};">
                <td style="padding: 12px; border: 1px solid #e5e7eb; width: 50%;">
                  <div style="font-size: 12px; color: hsl(var(--muted-foreground)); margin-bottom: 4px;">Expected Completion % (Today)</div>
                  <div style="font-size: 20px; font-weight: 600; color: #6b7280;">${Math.round(projectHealth.expectedCompletion)}%</div>
                </td>
                <td style="padding: 12px; border: 1px solid #e5e7eb; width: 50%;">
                  <div style="font-size: 12px; color: hsl(var(--muted-foreground)); margin-bottom: 4px;">Actual Completion %</div>
                  <div style="font-size: 20px; font-weight: 700; color: ${projectHealth.completionGap >= 0 ? '#16a34a' : '#dc2626'};">
                    ${Math.round(projectHealth.overallCompletion)}%
                    <span style="font-size: 12px; margin-left: 8px;">
                      ${projectHealth.completionGap >= 0 
                        ? `+${Math.round(projectHealth.completionGap)}% 🎯` 
                        : `${Math.round(projectHealth.completionGap)}% ⚠️`}
                    </span>
                  </div>
                </td>
              </tr>
              <!-- Schedule Status & Risk Level Row -->
              <tr style="border: 1px solid #e5e7eb;">
                <td style="padding: 12px; border: 1px solid #e5e7eb; background: white;">
                  <div style="font-size: 12px; color: hsl(var(--muted-foreground)); margin-bottom: 8px;">Schedule Status</div>
                  <div>${getStatusBadge(projectHealth.scheduleStatus)}</div>
                </td>
                <td style="padding: 12px; border: 1px solid #e5e7eb; background: white;">
                  <div style="font-size: 12px; color: hsl(var(--muted-foreground)); margin-bottom: 8px;">Risk Level</div>
                  <div>${getRiskBadge(projectHealth.riskLevel)}</div>
                </td>
              </tr>
              <!-- Item Distribution Row -->
              <tr style="border: 1px solid #e5e7eb;">
                <td colspan="2" style="padding: 12px; border: 1px solid #e5e7eb; background: white;">
                  <div style="font-size: 12px; color: hsl(var(--muted-foreground)); margin-bottom: 8px;">Item Distribution</div>
                  <div style="font-size: 11px; color: hsl(var(--foreground)); display: flex; gap: 16px;">
                    <div>✓ On Track: ${projectHealth.onTrackCount}</div>
                    <div>⚠ At Risk: ${projectHealth.atRiskCount}</div>
                    <div>🚨 Delayed: ${projectHealth.delayedCount}</div>
                  </div>
                </td>
              </tr>
              <!-- Statistics Summary Row (conditional) -->
              ${data.settings.showStatistics ? `
              <tr style="border-top: 2px solid #3b82f6;">
                <td colspan="2" style="padding: 16px; border: 1px solid #e5e7eb; background: #f8fafc;">
                  <div style="font-size: 13px; font-weight: 600; color: hsl(var(--foreground)); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                    📈 Overall Statistics
                  </div>
                  <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; font-size: 12px;">
                    <div>
                      <div style="color: hsl(var(--muted-foreground)); margin-bottom: 4px;">Total Items</div>
                      <div style="font-weight: 600; font-size: 18px; color: hsl(var(--foreground));">${data.statistics.totalItems}</div>
                    </div>
                    <div>
                      <div style="color: hsl(var(--muted-foreground)); margin-bottom: 4px;">Time Range</div>
                      <div style="font-weight: 600; font-size: 18px; color: hsl(var(--foreground));">${data.timelineRange.totalDays} days</div>
                    </div>
                    <div>
                      <div style="color: hsl(var(--muted-foreground)); margin-bottom: 4px;">Avg Duration</div>
                      <div style="font-weight: 600; font-size: 18px; color: hsl(var(--foreground));">${data.statistics.averageDuration} days</div>
                    </div>
                  </div>
                </td>
              </tr>
              ` : ''}
            </tbody>
          </table>
        </div>
        ` : ''}
        
        ${showCategoryPerformance && categoryPerformance && categoryPerformance.length > 0 ? `
        <!-- Category Performance -->
          <div style="background: hsl(var(--muted) / 0.3); padding: 20px; border-radius: 8px; margin-bottom: 20px;">
            <h3 style="font-size: 16px; font-weight: 600; margin-bottom: 16px; color: hsl(var(--foreground));">📈 Category Performance Analysis</h3>
            <table style="width: 100%; border-collapse: collapse; margin: 0 auto;">
              <thead>
                <tr style="background: #f9fafb;">
                  <th style="border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 12px; font-weight: 600;">Category</th>
                  <th style="border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 12px; font-weight: 600;">Expected</th>
                  <th style="border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 12px; font-weight: 600;">Actual</th>
                  <th style="border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 12px; font-weight: 600;">Items</th>
                  <th style="border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 12px; font-weight: 600;">Avg Duration</th>
                </tr>
              </thead>
              <tbody>
                ${categoryPerformance.map(cat => {
                  const gapColor = cat.completionGap >= 0 ? '#16a34a' : '#dc2626';
                  const gapBg = cat.completionGap >= 0 ? '#f0fdf4' : '#fef2f2';
                  return `
                  <tr style="background: white;">
                    <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 12px; font-weight: 600;">${cat.category}</td>
                    <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 11px; color: #6b7280;">${Math.round(cat.expectedCompletion)}%</td>
                    <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 12px; background: ${gapBg};">
                      <strong style="color: ${gapColor};">${Math.round(cat.completionPercentage)}%</strong>
                      <span style="font-size: 10px; margin-left: 4px;">
                        ${cat.completionGap >= 0 
                          ? `(+${Math.round(cat.completionGap)}% 🎯)` 
                          : `(${Math.round(cat.completionGap)}% ⚠️)`}
                      </span>
                    </td>
                    <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 11px;">${cat.totalItems} (✓${cat.onTrackCount} ⚠${cat.atRiskCount} 🚨${cat.delayedCount})</td>
                    <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 11px;">${cat.averageDuration} days</td>
                  </tr>
                `;
                }).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}
        
        ${showIssues && issues && issues.length > 0 ? `
        <!-- Issues & Recommendations -->
          <div style="background: hsl(var(--muted) / 0.3); padding: 20px; border-radius: 8px;">
            <h3 style="font-size: 16px; font-weight: 600; margin-bottom: 16px; color: hsl(var(--foreground));">⚠️ Issues & Recommendations</h3>
            <table style="width: 100%; border-collapse: collapse; margin: 0 auto;">
              <thead>
                <tr style="background: #f9fafb;">
                  <th style="border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 12px; font-weight: 600; width: 15%;">Severity</th>
                  <th style="border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 12px; font-weight: 600; width: 40%;">Issue</th>
                  <th style="border: 1px solid #e5e7eb; padding: 10px; text-align: left; font-size: 12px; font-weight: 600; width: 45%;">Recommendation</th>
                </tr>
              </thead>
              <tbody>
                ${issues.map(issue => {
                  const severityColors = {
                    'critical': '#ef4444',
                    'warning': '#f59e0b',
                    'info': '#3b82f6'
                  };
                  const severityLabels = {
                    'critical': 'CRITICAL',
                    'warning': 'WARNING',
                    'info': 'INFO'
                  };
                  return `
                    <tr style="background: white; border-left: 4px solid ${severityColors[issue.severity as keyof typeof severityColors]};">
                      <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 11px;">
                        <span style="display: inline-block; background: ${severityColors[issue.severity as keyof typeof severityColors]}; color: white; padding: 4px 8px; border-radius: 4px; font-size: 10px; font-weight: 600;">${severityLabels[issue.severity as keyof typeof severityLabels]}</span>
                      </td>
                      <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 12px; font-weight: 600; color: hsl(var(--foreground));">${issue.message}</td>
                      <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 11px; color: hsl(var(--muted-foreground));">${issue.recommendation ? `💡 ${issue.recommendation}` : '-'}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}
      </div>
    `;
  };

  // Generate Milestone Summary Timeline HTML — uses shared layout engine
  const generateMilestoneSummaryHTML = (data: TimelineReportExportData) => {
    const summaryMilestones = data.summaryMilestones || [];
    if (summaryMilestones.length === 0) return '';

    // Design width for layout math — wider than render box to amplify connector contrast.
    // Box renders fill-width (100%); positions are converted to % of totalWidthPx.
    const EXPORT_DESIGN_WIDTH = 1200;

    // Sort milestones by date (same order stepper uses)
    const sorted = [...summaryMilestones].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // STEPPER layout — shared with Preview for pixel-consistent rendering
    const layout = computeMilestoneStepperLayout({
      milestones: sorted,
      containerWidthPx: EXPORT_DESIGN_WIDTH,
      slotW: 110,
      minConn: 24,
      autoFitMode: 'shrink',
    });

    const { nodes, totalWidthPx } = layout;
    if (nodes.length === 0 || totalWidthPx <= 0) return '';

    const toPct = (px: number) => (px / totalWidthPx) * 100;
    const centerPct = nodes.map((n) => toPct(n.centerPx));
    const leftPct = nodes.map((n) => toPct(n.slotLeftPx));
    const widthPct = nodes.map((n) => toPct(n.slotW));

    const firstC = centerPct[0];
    const lastC = centerPct[centerPct.length - 1];

    // Vertical layout (px)
    const DATE_H = 16;
    const BAR_H = 18;
    const DUR_H = 14;
    const NOTE_H = 48;
    const dateTop = 0;
    const barTop = DATE_H;
    const durTop = DATE_H + BAR_H;
    const noteTop = DATE_H + BAR_H + DUR_H;
    const totalH = noteTop + NOTE_H;

    return `
      <div class="milestone-summary-section" style="margin-bottom: 20px;">
        <div style="position: relative; padding: 8px 12px; background: #fafafa; border: 1px solid #e5e7eb; border-radius: 4px; overflow: hidden;">
          <div style="position: relative; width: 100%; height: ${totalH}px;">

            <!-- 1. Date labels -->
            ${sorted.map((m, i) => `
                <div style="position: absolute;
                  left: ${leftPct[i]}%;
                  top: ${dateTop}px;
                  width: ${widthPct[i]}%;
                  height: ${DATE_H}px;
                  line-height: ${DATE_H}px;
                  text-align: center;
                  font-size: 9px; font-weight: 700; color: #1f2937;
                  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  ${format(new Date(m.date), 'dd MMM yy').toUpperCase()}
                </div>
              `).join('')}

            <!-- 2. Bar + dots -->
            ${nodes.length > 1 ? `
              <div style="position: absolute;
                left: ${firstC}%;
                width: ${Math.max(0, lastC - firstC)}%;
                top: ${barTop + BAR_H / 2 - 3}px;
                height: 6px; background: #9ca3af; border-radius: 3px;"></div>
            ` : ''}
            ${sorted.map((m, i) => `
                <div style="position: absolute;
                  left: ${centerPct[i]}%;
                  top: ${barTop + BAR_H / 2 - 7}px;
                  width: 14px; height: 14px;
                  margin-left: -7px;
                  background: ${m.color}; border: 2px solid white; border-radius: 50%;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 10;"></div>
              `).join('')}

            <!-- 3. Duration labels -->
            ${sorted.map((m, i) => {
              if (i >= sorted.length - 1) return '';
              const node = nodes[i];
              if (node.daysToNext == null) return '';
              const left = centerPct[i];
              const width = Math.max(0, centerPct[i + 1] - centerPct[i]);
              return `
                <div style="position: absolute;
                  left: ${left}%;
                  top: ${durTop}px;
                  width: ${width}%;
                  height: ${DUR_H}px;
                  line-height: ${DUR_H}px;
                  text-align: center;
                  font-size: 9px; color: #6b7280; white-space: nowrap;">
                  (${node.daysToNext} Days)
                </div>
              `;
            }).join('')}

            <!-- 4. Note labels -->
            ${sorted.map((m, i) => `
                <div style="position: absolute;
                  left: ${leftPct[i]}%;
                  top: ${noteTop}px;
                  width: ${widthPct[i]}%;
                  min-height: ${NOTE_H}px;
                  text-align: center;
                  font-size: 9px; font-weight: 600; color: #1f2937;
                  line-height: 1.3;
                  word-wrap: break-word;
                  overflow-wrap: break-word;">
                  ${getMilestoneDisplayNote(m)}
                </div>
              `).join('')}
          </div>
        </div>
      </div>
    `;
  };

  // Generate simple HTML without React components to avoid SSR issues
  const generateSimpleTimelineHTML = (data: TimelineReportExportData) => {
    const isMultiProject = data.isMultiProject;
    const showLogo = data.settings.showLogo ?? true;
    const logoType = data.settings.logoType || 'solid';
    const logoUrl = logoType === 'enp' 
      ? enpLogo
      : 'https://storage.googleapis.com/gpt-engineer-file-uploads/5Ahiblr9aqebkhCMcYkI21PicVv2/social-images/social-1758241299872-solid logo.png';

    // PART B/C: shared stats counter — render pages FIRST so header can display total drawn edges.
    const exportStats: ExportStats = { edgesDrawn: 0 };
    const pagesHTML = data.pages.map(page => generateTimelinePageHTML(page, data, exportStats)).join('');

    // Hybrid approach: Separate report header from timeline chunks
    return `
      <div class="printable-timeline-layout">
        <!-- Report Header Section (appears once) -->
        <div class="report-header-section mb-6" style="padding: 0 8px;">
          <div class="flex justify-between items-start mb-4">
            <!-- Left: Company Logo + Titles -->
            <div class="flex items-center gap-4">
              ${showLogo ? `
                <img 
                  src="${logoUrl}" 
                  alt="Company Logo" 
                  style="height: 48px; width: 48px; object-fit: contain; flex-shrink: 0;"
                />
              ` : ''}
              <div>
                <h2 style="font-size: 20px; font-weight: 700; color: hsl(var(--foreground)); white-space: nowrap;">
                  ${data.settings.titlePrefix || data.settings.title || 'Timeline Report'} - ${data.projectName}
                </h2>
              </div>
            </div>
            
            <!-- Right: Project Logo -->
            ${data.projectImageUrl ? `
              <img 
                src="${data.projectImageUrl}" 
                alt="Project Logo" 
                style="height: 64px; width: 64px; object-fit: contain; flex-shrink: 0; border-radius: 4px;"
              />
            ` : ''}
          </div>
          
          <!-- Assessment sections (if any) -->
          ${generateAssessmentHTML(data)}
        </div>
        
        <!-- NEW: Milestone Summary Timeline -->
        ${generateMilestoneSummaryHTML(data)}
        
        <!-- Timeline Content Section (chunks with repeating headers) -->
        <div class="timeline-content-section">
          ${pagesHTML}
        </div>
      </div>
    `;
  };


  // Calculate responsive text size based on day width - maximize readability
  const getHeaderTextSize = (dayWidth: number): string => {
    if (dayWidth >= 15) return 'text-[10px]'; // Extra wide columns
    if (dayWidth >= 12) return 'text-[8px]';  // Wide columns
    if (dayWidth >= 9) return 'text-[7px]';   // Medium-wide columns
    if (dayWidth >= 6) return 'text-[6px]';   // Medium columns
    return 'text-[5px]'; // Narrow columns (minimum)
  };

  // Calculate actual timeline content width based on visible columns
  const calculateTimelineContentWidth = (data: TimelineReportExportData, page?: any): number => {
    let width = 0;
    
    width += 240; // Item name
    width += 35;  // % completion
    
    if (data.isMultiProject) {
      width += 100; // Project column
    }
    
    if (data.settings.showCategories) {
      width += 80; // Category column
    }
    
    width += 35;  // Duration
    width += 55;  // Start date
    width += 55;  // End date
    
    // Calculate timeline width from page-specific data or global data
    const pageDays = page ? 
      Math.ceil((new Date(page.endDate).getTime() - new Date(page.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1 :
      (data.timelineRange?.totalDays || 30);
    const dayWidth = page?.dayWidth || data.pages[0]?.dayWidth || 10;
    const timelineDaysWidth = dayWidth * pageDays;
    
    // REMOVED 650px cap - now shows full timeline range
    width += timelineDaysWidth;
    
    return width;
  };


  const generateTimelinePageHTML = (page: any, data: TimelineReportExportData, exportStats?: ExportStats) => {
    const dateColumns = [];
    const start = new Date(page.startDate);
    const end = new Date(page.endDate);
    const today = ymdLocal(new Date());
    const headerTextSize = getHeaderTextSize(page.dayWidth);
    const timelineContentWidth = calculateTimelineContentWidth(data, page);
    
    // Logo configuration
    const showLogo = data.settings.showLogo ?? true;
    const logoType = data.settings.logoType || 'solid';
    const logoUrl = logoType === 'enp' 
      ? enpLogo
      : 'https://storage.googleapis.com/gpt-engineer-file-uploads/5Ahiblr9aqebkhCMcYkI21PicVv2/social-images/social-1758241299872-solid logo.png';
    
    while (start <= end) {
      const dateStr = ymdLocal(start);
      dateColumns.push({
        date: dateStr,
        dayOfWeek: start.getDay(),
        isWeekend: start.getDay() === 0 || start.getDay() === 6,
        isMonthStart: start.getDate() === 1,
        isToday: dateStr === today
      });
      start.setDate(start.getDate() + 1);
    }

    // Type definition for flat rows
    type FlatRow = 
      | { type: 'item'; item: any; isFirstOfItem: true }
      | { type: 'milestone'; milestone: any; parentItem: any; isFirstOfItem: false }
      | { type: 'group'; groupName: string; count: number; gStart: string | null; gEnd: string | null; gDur: number; gPct: number; isFirstOfItem: false };

    // Flatten items + milestones into a single array of rows
    const flattenItemsToRows = (items: any[]): FlatRow[] => {
      const rows: FlatRow[] = [];
      const overlappingItems = items.filter(item => {
        // ✅ Filter items: Check for overlap with page date range
        const itemStart = ymdLocal(new Date(item.startDate));
        const itemEnd = ymdLocal(new Date(item.endDate));
        const pageStart = dateColumns[0]?.date;
        const pageEnd = dateColumns[dateColumns.length - 1]?.date;

        // Skip items completely outside the visible date range
        // Overlap check: itemStart <= pageEnd AND itemEnd >= pageStart
        const hasOverlap = itemStart <= pageEnd && itemEnd >= pageStart;
        return hasOverlap;
      });

      const hasAssignedGroup = overlappingItems.some(item => (item.group_name || '').trim());
      const distinctGroups = new Set(overlappingItems.map(item => item.group_name || '')).size;
      const shouldShowGroupRows = !data.isMultiProject
        && (data.settings as any).sortOrder !== 'category'
        && hasAssignedGroup
        && distinctGroups > 1;

      overlappingItems.forEach((item, itemIndex) => {
        const groupName = item.group_name || '';
        const previousGroupName = itemIndex > 0 ? (overlappingItems[itemIndex - 1].group_name || '') : null;
        if (shouldShowGroupRows && groupName !== previousGroupName) {
          let count = 1;
          while (
            itemIndex + count < overlappingItems.length
            && (overlappingItems[itemIndex + count].group_name || '') === groupName
          ) {
            count++;
          }
          const groupItems = overlappingItems.slice(itemIndex, itemIndex + count);
          const gStarts = groupItems.map(i => i.startDate).filter(Boolean).sort();
          const gEnds = groupItems.map(i => i.endDate).filter(Boolean).sort();
          const gStart = gStarts[0] ?? null;
          const gEnd = gEnds[gEnds.length - 1] ?? null;
          const dayDiff = (a: string, b: string) =>
            Math.ceil((new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24)) + 1;
          const gDur = (gStart && gEnd) ? dayDiff(gStart, gEnd) : 0;
          let sumW = 0, sumP = 0;
          for (const it of groupItems) {
            const w = (it.startDate && it.endDate) ? dayDiff(it.startDate, it.endDate) : 1;
            sumW += w;
            sumP += w * (it.completion_percentage ?? 0);
          }
          const gPct = sumW > 0 ? Math.round(sumP / sumW) : 0;
          rows.push({
            type: 'group',
            groupName: groupName || 'Chưa phân nhóm',
            count,
            gStart,
            gEnd,
            gDur,
            gPct,
            isFirstOfItem: false
          });
        }

        // Add item row (now guaranteed to overlap with visible range)
        rows.push({ type: 'item', item, isFirstOfItem: true });
        
        // Add milestone rows if enabled
        if (data.settings.showMilestones !== false) {
          const pageStart = dateColumns[0]?.date;
          const pageEnd = dateColumns[dateColumns.length - 1]?.date;
          
          const activeMsTypes = data.settings.milestoneFilters?.activeMilestoneTypes;
          const hasMsTypeFilter = activeMsTypes && activeMsTypes.length > 0;
          
          const itemMilestones = (data.milestonesByItem?.[item.id] || []).filter(m => {
            if (!m.name) return false;
            
            // Lọc theo loại mốc (mirror web): rỗng → giữ; có chọn → chỉ mốc thuộc loại đó
            if (hasMsTypeFilter && !activeMsTypes.includes(m.milestone_type || 'default')) {
              return false;
            }
            
            // ✅ Filter: Check for overlap with page date range
            const milestoneStart = ymdLocal(new Date(m.start_date));
            const milestoneEnd = m.end_date ? ymdLocal(new Date(m.end_date)) : milestoneStart;
            
            // Overlap check: milestoneStart <= pageEnd AND milestoneEnd >= pageStart
            const hasOverlap = milestoneStart <= pageEnd && milestoneEnd >= pageStart;
            return hasOverlap;
          });
          
          itemMilestones.forEach(milestone => {
            rows.push({ 
              type: 'milestone', 
              milestone, 
              parentItem: item, 
              isFirstOfItem: false 
            });
          });
        }
      });
      
      return rows;
    };

    // Utility: Chunk flat rows by row count (no grouping logic)
    const chunkRowsForPrint = (rows: FlatRow[], rowsFirstPage: number, rowsSubsequent: number) => {
      const chunks: FlatRow[][] = [];
      let currentChunk: FlatRow[] = [];
      let isFirstChunk = true;

      rows.forEach(row => {
        const currentPageLimit = isFirstChunk ? rowsFirstPage : rowsSubsequent;
        
        // Simple: each row = 1 row, no need to calculate item groups
        if (currentChunk.length >= currentPageLimit) {
          chunks.push(currentChunk);
          currentChunk = [];
          isFirstChunk = false;
        }
        
        currentChunk.push(row);
      });

      // Add last chunk if not empty
      if (currentChunk.length > 0) {
        chunks.push(currentChunk);
      }

      // If there are no rows, return an empty array (no chunks)
      return chunks.length > 0 ? chunks : [];
    };

    // Helper: Render timeline header
    const renderTimelineHeader = () => `
      <div class="timeline-header border-b mb-4">
        <div class="flex">
          <div style="width: 28px; min-width: 28px; max-width: 28px; background-color: #f3f4f6; height: 60px; display: flex; align-items: center; justify-content: center; border-right: 1px solid #e5e7eb; font-size: 8px; font-weight: 600; color: #6b7280;">STT</div>
          <div style="width: 24px; min-width: 24px; max-width: 24px; background-color: #f3f4f6; height: 60px; display: flex; align-items: center; justify-content: center; border-right: 1px solid #e5e7eb;"></div>
          <div class="w-[240px] p-2 font-medium text-xs border-r" style="background-color: #f3f4f6; height: 60px; display: flex; align-items: center; justify-content: flex-start; min-width: 240px; max-width: 240px;">Item</div>
          <div class="w-[35px] p-2 font-medium text-xs border-r" style="background-color: #f3f4f6; height: 60px; display: flex; align-items: center; justify-content: center; min-width: 35px; max-width: 35px;">%</div>
          ${data.isMultiProject ? '<div class="w-[100px] p-2 font-medium text-xs border-r" style="background-color: #f3f4f6; height: 60px; display: flex; align-items: center; justify-content: flex-start; min-width: 100px; max-width: 100px;">Dự án</div>' : ''}
          ${data.settings.showCategories ? '<div class="w-[80px] p-2 font-medium text-xs border-r" style="background-color: #f3f4f6; height: 60px; display: flex; align-items: center; justify-content: flex-start; min-width: 80px; max-width: 80px;">Category</div>' : ''}
          <div class="w-[35px] p-2 font-medium text-xs border-r" style="background-color: #f3f4f6; height: 60px; display: flex; align-items: center; justify-content: center;">Dur</div>
          <div class="w-[55px] min-w-[55px] p-1 font-medium text-[9px] border-r" style="background-color: #f3f4f6; height: 60px; display: flex; align-items: center; justify-content: center;">Start</div>
          <div class="w-[55px] min-w-[55px] p-1 font-medium text-[9px] border-r" style="background-color: #f3f4f6; height: 60px; display: flex; align-items: center; justify-content: center;">End</div>
          <div class="flex overflow-x-auto">
            ${dateColumns.map(col => {
              // Calculate total days for smart display logic
              const totalDays = data?.timelineRange?.totalDays ?? (Math.floor((new Date(page.endDate).getTime() - new Date(page.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1);
              
              // Smart vertical line display for long timelines
              const isMonday = col.dayOfWeek === 1;
              const borderClass = col.isMonthStart ? 'timeline-month-line-header' : 
                                 (totalDays > 150 && isMonday) ? 'timeline-week-line' :
                                 (totalDays <= 150 && col.isWeekend) ? 'timeline-weekend-line' : 
                                 (totalDays <= 150 ? 'timeline-grid-line' : '');
              
              const d = new Date(`${col.date}T00:00:00`);
              const day = d.getDate();
              
              // Smart date display based on timeline length
              const shouldShow = totalDays <= 45 
                ? true 
                : totalDays <= 150 
                  ? [1, 5, 10, 15, 20, 25].includes(day)
                  : day === 1;
              
              // Format: dd-MM-yy for day 1, dd for other days
              const label = day === 1 
                ? `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear().toString().slice(-2)}`
                : format(d, 'dd');
              
              return `
              <div class="flex-shrink-0 ${headerTextSize} leading-none text-center ${borderClass} ${col.isWeekend ? 'bg-muted/80' : ''} ${col.isMonthStart ? 'bg-primary/10' : ''} ${col.isToday ? 'bg-primary/10' : ''}" style="width: ${page.dayWidth}px; height: 60px; display: flex; align-items: center; justify-content: center; background-color: #f3f4f6;">
                ${shouldShow 
                  ? `<div class="transform -rotate-90 origin-center whitespace-nowrap font-semibold" style="padding: 2px 6px; font-size: 10px;">${label}</div>`
                  : `<div class="transform -rotate-90 origin-center whitespace-nowrap" style="visibility:hidden;">&nbsp;</div>`
                }
              </div>
            `}).join('')}
          </div>
        </div>
      </div>
    `;

    // Helper: Render continuation header for milestones starting a new page
    const renderContinuationHeader = (parentItem: any) => `
      <div class="flex border-b items-center" style="background-color: rgba(229, 231, 235, 0.3); padding: 4px 0;">
        <div style="width: 28px; min-width: 28px; max-width: 28px; border-right: 1px solid #e5e7eb;">&nbsp;</div>
        <div style="width: 24px; min-width: 24px; max-width: 24px; border-right: 1px solid #e5e7eb;">&nbsp;</div>
        <div class="w-[240px] p-2 text-xs border-r truncate flex items-center italic text-muted-foreground" style="min-width: 240px; max-width: 240px;">
          ↳ ${parentItem.name} (tiếp)
        </div>
        <div class="w-[35px] p-2 text-[8px] border-r text-center flex items-center justify-center" style="min-width: 35px; max-width: 35px;">&nbsp;</div>
        ${data.isMultiProject ? '<div class="w-[100px] p-2 text-xs border-r" style="min-width: 100px; max-width: 100px;">&nbsp;</div>' : ''}
        ${data.settings.showCategories ? '<div class="w-[80px] p-2 text-xs border-r" style="min-width: 80px; max-width: 80px;">&nbsp;</div>' : ''}
        <div class="w-[35px] p-2 text-[8px] border-r text-center flex items-center justify-center">&nbsp;</div>
        <div class="w-[55px] min-w-[55px] p-1 text-[8px] border-r text-center flex items-center justify-center">&nbsp;</div>
        <div class="w-[55px] min-w-[55px] p-1 text-[8px] border-r text-center flex items-center justify-center">&nbsp;</div>
        <div class="flex-1 relative overflow-hidden">&nbsp;</div>
      </div>
    `;

    // Helper: Render flat rows (items and milestones)
    let itemIdx = 0;
    let msIdx = 0;
    const renderFlatRows = (rows: FlatRow[]) => rows.map((row, rowIndex) => {
      if (row.type === 'group') {
        const gsIdx = row.gStart ? dateColumns.findIndex(c => c.date === ymdLocal(new Date(row.gStart))) : -1;
        const geIdx = row.gEnd ? dateColumns.findIndex(c => c.date === ymdLocal(new Date(row.gEnd))) : -1;
        const gs = gsIdx >= 0 ? gsIdx : 0;
        const ge = geIdx >= 0 ? geIdx : dateColumns.length - 1;
        return `
          <div class="flex border-b items-center" style="background-color: rgba(229,231,235,0.55);">
            <div style="width: 28px; min-width: 28px; max-width: 28px; border-right: 1px solid #e5e7eb;">&nbsp;</div>
            <div style="width: 24px; min-width: 24px; max-width: 24px; border-right: 1px solid #e5e7eb;">&nbsp;</div>
            <div class="w-[240px] p-2 border-r truncate flex items-center" style="min-width: 240px; max-width: 240px;">
              <span style="font-weight:700;font-size:13px;">${row.groupName}</span>
              <span style="font-size:8px;color:#6b7280;margin-left:6px;">${row.count} việc</span>
            </div>
            <div class="w-[35px] p-2 text-[8px] border-r text-center text-muted-foreground font-semibold flex items-center justify-center" style="min-width: 35px; max-width: 35px;">${row.gPct > 0 ? `${row.gPct}%` : '&nbsp;'}</div>
            ${data.isMultiProject ? '<div class="w-[100px] p-2 text-xs border-r" style="min-width: 100px; max-width: 100px;">&nbsp;</div>' : ''}
            ${data.settings.showCategories ? '<div class="w-[80px] p-2 text-xs border-r" style="min-width: 80px; max-width: 80px;">&nbsp;</div>' : ''}
            <div class="w-[35px] p-2 text-[8px] border-r text-center text-muted-foreground font-semibold flex items-center justify-center">${row.gDur > 0 ? `${row.gDur}d` : '&nbsp;'}</div>
            <div class="w-[55px] min-w-[55px] p-1 text-[8px] border-r text-center text-muted-foreground whitespace-nowrap font-semibold flex items-center justify-center">
              ${row.gStart ? format(new Date(row.gStart), 'dd-MM-yy') : '&nbsp;'}
            </div>
            <div class="w-[55px] min-w-[55px] p-1 text-[8px] border-r text-center text-muted-foreground whitespace-nowrap font-semibold flex items-center justify-center">
              ${row.gEnd ? format(new Date(row.gEnd), 'dd-MM-yy') : '&nbsp;'}
            </div>
            <div class="flex-1 relative overflow-hidden" style="min-height: 24px;">
              <div class="absolute inset-0 flex" style="z-index: 0;">
                ${dateColumns.map(col => {
                  const totalDays = data?.timelineRange?.totalDays ?? (Math.floor((new Date(page.endDate).getTime() - new Date(page.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1);
                  const bgClass = col.isToday ? 'bg-primary/10' : col.isWeekend ? 'bg-muted/70' : '';
                  const isMonday = col.dayOfWeek === 1;
                  const borderClass = col.isMonthStart ? 'timeline-month-line' :
                                     (totalDays > 150 && isMonday) ? 'timeline-week-line' :
                                     (totalDays <= 150 && col.isWeekend) ? 'timeline-weekend-line' :
                                     (totalDays <= 150 ? 'timeline-grid-line' : '');
                  return `<div class="flex-shrink-0 ${bgClass} ${borderClass}" style="width: ${page.dayWidth}px; height: 100%; box-sizing: border-box;"></div>`;
                }).join('')}
              </div>
              ${row.gStart && row.gEnd ? `
                <div class="print-timeline-bar" style="
                  left: ${gs * page.dayWidth}px;
                  width: ${Math.max(page.dayWidth, (ge - gs + 1) * page.dayWidth)}px;
                  z-index: 10;
                ">
                  <div style="
                    position: absolute;
                    inset: 0;
                    background-image: repeating-linear-gradient(45deg, #64748B 0 1px, transparent 1px 5px);
                    opacity: .45;
                    print-color-adjust: exact;
                  "></div>
                  <div style="
                    position: absolute;
                    left: 0;
                    top: 0;
                    height: 100%;
                    width: ${row.gPct}%;
                    background-color: #64748B;
                    opacity: .3;
                  "></div>
                  <div style="position: absolute; top: 0; left: 0; right: 0; height: 2px; background-color: #94A3B8;"></div>
                  <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 2px; background-color: #94A3B8;"></div>
                  <div style="position: absolute; top: 0; bottom: 0; left: 0; width: 2px; background-color: #94A3B8;"></div>
                  <div style="position: absolute; top: 0; bottom: 0; right: 0; width: 2px; background-color: #94A3B8;"></div>
                </div>
              ` : ''}
            </div>
          </div>
        `;
      }

      // If this is a milestone row at the start of a chunk, add continuation header
      const needsContinuationHeader = rowIndex === 0 && row.type === 'milestone';

      let sttLabel = '';
      if (row.type === 'item') {
        itemIdx++;
        msIdx = 0;
        sttLabel = String(itemIdx);
      } else {
        msIdx++;
        sttLabel = `${itemIdx}.${msIdx}`;
      }
      
      if (row.type === 'item') {
        const item = row.item;
        const categoryColor = data.statistics.categoryBreakdown.find(c => c.category === item.category)?.color || '#6b7280';
        
        // Find exact column index for start and end dates to ensure alignment
        const itemStartDate = ymdLocal(new Date(item.startDate));
        const itemEndDate = ymdLocal(new Date(item.endDate));
        const startColumnIndex = dateColumns.findIndex(col => col.date === itemStartDate);
        const endColumnIndex = dateColumns.findIndex(col => col.date === itemEndDate);
        
        // Adjust indices for items that may extend beyond page boundaries
        // If start is before page, clamp to first column; if end is after page, clamp to last column
        const adjustedStartIndex = startColumnIndex >= 0 ? startColumnIndex : 0;
        const adjustedEndIndex = endColumnIndex >= 0 ? endColumnIndex : dateColumns.length - 1;
        
        // Use adjusted indices for timeline bar rendering
        const startOffset = adjustedStartIndex;
        const duration = adjustedEndIndex - adjustedStartIndex + 1;
        
        // Calculate actual duration in days for display
        const durationDays = Math.ceil((new Date(item.endDate).getTime() - new Date(item.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1;
        
        // Get milestones for this item (with highlight flag, not filtered out)
        const itemMilestones = (data.milestonesByItem?.[item.id] || [])
          .filter(m => m.name)
          .map(m => {
            // Calculate if this milestone matches filters (for highlighting)
            const typeMatch = !data.settings.milestoneFilters?.activeMilestoneTypes?.length ||
                             data.settings.milestoneFilters.activeMilestoneTypes.includes(m.milestone_type || '');
            const nameMatch = !data.settings.milestoneFilters?.milestoneName?.trim() ||
                             m.name?.toLowerCase().includes(
                               data.settings.milestoneFilters.milestoneName.trim().toLowerCase()
                             );
            return {
              ...m,
              isHighlighted: typeMatch && nameMatch
            };
          });
        const showMilestones = data.settings.showMilestones !== false && itemMilestones.length > 0;
        
        const hlStyle = data.settings.highlightStyle || HIGHLIGHT_BAR_STYLE_DEFAULTS;
        // Check if this item is selected for summary
        const isItemInSummary = data.summaryMilestones?.some(
          sm => (sm.type === 'item' && sm.sourceItemId === item.id)
        );
        const itemHighlightStyle = '';
        const itemBarBg = isItemInSummary
          ? hexToRgba(hlStyle.fill_color, hlStyle.fill_opacity)
          : (data.settings.showCategoryColors ? categoryColor : '#6b7280');
        const itemBarBorder = isItemInSummary && hlStyle.border_enabled
          ? `${hlStyle.border_width}px dashed ${hlStyle.border_color}`
          : 'none';
        
        return `
          <div class="flex border-b items-center" style="${itemHighlightStyle}">

            ${renderIndexCellHTML(sttLabel)}
            ${getAvatarCellHTML(item.assigned_user_id, data.teamMembers)}
            <div class="w-[240px] p-2 text-xs border-r truncate flex items-center" style="min-width: 240px; max-width: 240px;">${item.name}</div>
            <div class="w-[35px] p-2 text-[8px] border-r text-center font-medium flex items-center justify-center" style="min-width: 35px; max-width: 35px;">${item.completion_percentage ?? 0}%</div>
            ${data.isMultiProject ? `<div class="w-[100px] p-2 text-xs border-r truncate flex items-center" style="min-width: 100px; max-width: 100px;">${item.projectName || 'N/A'}</div>` : ''}
            ${data.settings.showCategories ? `
            <div class="w-[80px] p-2 text-xs border-r flex items-center" style="min-width: 80px; max-width: 80px;">
              ${data.settings.showCategoryColors ? `
                <div class="flex items-center gap-1">
                  <div class="w-2 h-2 rounded-full flex-shrink-0" style="background-color: ${categoryColor}"></div>
                  <span class="truncate text-[8px]">${item.category}</span>
                </div>
              ` : `<span class="truncate text-[8px]">${item.category}</span>`}
            </div>
             ` : ''}
             <div class="w-[35px] p-2 text-[8px] border-r text-center flex items-center justify-center">${durationDays}d</div>
        <div class="w-[55px] min-w-[55px] p-1 text-[8px] border-r text-center text-muted-foreground whitespace-nowrap flex items-center justify-center">
          ${format(new Date(item.startDate), 'dd-MM-yy')}
        </div>
        <div class="w-[55px] min-w-[55px] p-1 text-[8px] border-r text-center text-muted-foreground whitespace-nowrap flex items-center justify-center">
          ${format(new Date(item.endDate), 'dd-MM-yy')}
        </div>
             <div class="flex-1 relative overflow-hidden" style="min-height: ${showMilestones ? '32px' : '24px'};">
               <!-- Background columns with weekend/today highlighting -->
               <div class="absolute inset-0 flex" style="z-index: 0;">
                 ${dateColumns.map(col => {
                   const totalDays = data?.timelineRange?.totalDays ?? (Math.floor((new Date(page.endDate).getTime() - new Date(page.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1);
                   const bgClass = col.isToday ? 'bg-primary/10' : col.isWeekend ? 'bg-muted/70' : '';
                   const isMonday = col.dayOfWeek === 1;
                   const borderClass = col.isMonthStart ? 'timeline-month-line' : 
                                      (totalDays > 150 && isMonday) ? 'timeline-week-line' :
                                      (totalDays <= 150 && col.isWeekend) ? 'timeline-weekend-line' : 
                                      (totalDays <= 150 ? 'timeline-grid-line' : '');
                   return `<div class="flex-shrink-0 ${bgClass} ${borderClass}" style="width: ${page.dayWidth}px; height: 100%; box-sizing: border-box;"></div>`;
                 }).join('')}
               </div>
              <!-- Timeline bar wrapper with completion -->
              <div class="print-timeline-bar" style="
                left: ${startOffset * page.dayWidth}px;
                width: ${Math.max(page.dayWidth, duration * page.dayWidth)}px;
                z-index: 10;
                ${isItemInSummary ? `outline: ${itemBarBorder}; outline-offset: -${hlStyle.border_width}px;` : ''}
              ">
                <!-- Base bar (mờ) -->
                <div style="
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  background-color: ${isItemInSummary ? itemBarBg : (data.settings.showCategoryColors ? categoryColor : '#6b7280')};
                  opacity: ${isItemInSummary ? 1 : 0.3};
                  border-radius: 2px;
                "></div>
                
                <!-- Completion bar (đậm) -->
                ${!isItemInSummary && item.completion_percentage > 0 ? `
                <div style="
                  position: absolute;
                  top: 0;
                  left: 0;
                  height: 100%;
                  width: ${item.completion_percentage}%;
                  background-color: ${data.settings.showCategoryColors ? categoryColor : '#6b7280'};
                  opacity: 1;
                  border-radius: 2px;
                "></div>
                ` : ''}
              </div>

              
              <!-- Milestone Markers -->
              ${showMilestones ? itemMilestones.map((milestone: any) => {
                const milestoneDate = new Date(milestone.start_date);
                const milestoneDateStr = ymdLocal(milestoneDate);
                const milestoneIndex = dateColumns.findIndex(col => col.date === milestoneDateStr);
                if (milestoneIndex < 0) return '';
                
                 // Get milestone color from milestone_types data
                 const milestoneTypeRecord = data.milestoneTypes?.find(mt => mt.name === milestone.milestone_type);
                 const milestoneColor = milestoneTypeRecord?.color || '#6b7280';
                
                const isHighlighted = milestone.isHighlighted;
                const opacity = isHighlighted ? 1 : 0.7;
                
                  return `
                   <div 
                     class="absolute" 
                     style="
                       left: ${(milestoneIndex * page.dayWidth).toFixed(2)}px;
                       top: 50%;
                       transform: translateY(-50%);
                       z-index: ${isHighlighted ? 20 : 15};
                       display: flex;
                       align-items: center;
                       justify-content: center;
                       opacity: ${opacity};
                     "
                     title="${milestone.name}"
                   >
                     ${getMilestoneStatusIconSVG(milestone.status || 'pending', milestoneColor)}
                   </div>
                  `;
              }).join('') : ''}
             </div>
          </div>
        `;
      } else {
        // Milestone row
        const milestone = row.milestone;
        const parentItem = row.parentItem;
        const milestoneStartDate = ymdLocal(new Date(milestone.start_date));
        const milestoneEndDate = milestone.end_date 
          ? ymdLocal(new Date(milestone.end_date)) 
          : milestoneStartDate;
        
        const startIndex = dateColumns.findIndex(col => col.date === milestoneStartDate);
        const endIndex = dateColumns.findIndex(col => col.date === milestoneEndDate);
        
        // Adjust indices for milestones that may extend beyond page boundaries
        // If start is before page, clamp to first column; if end is after page, clamp to last column
        const adjustedStartIndex = startIndex >= 0 ? startIndex : 0;
        const adjustedEndIndex = endIndex >= 0 ? endIndex : dateColumns.length - 1;
        
        // Check if milestone extends beyond page boundaries
        const startsBeforePage = startIndex < 0;
        const endsAfterPage = endIndex < 0;
        const partialIndicator = startsBeforePage ? '◀ ' : (endsAfterPage ? ' ▶' : '');
        
        // Get milestone color from milestone_types data
        const milestoneTypeRecord = data.milestoneTypes?.find(mt => mt.name === milestone.milestone_type);
        const milestoneColor = milestoneTypeRecord?.color || '#6b7280';
        
        const milestoneDuration = adjustedEndIndex - adjustedStartIndex + 1;
        const durationDays = Math.ceil(
          (new Date(milestone.end_date || milestone.start_date).getTime() - 
           new Date(milestone.start_date).getTime()) / (1000 * 60 * 60 * 24)
        ) + 1;
        
        // Check if milestone is selected for summary (3-key match: bare id, start-prefixed, end-prefixed)
        const isMilestoneInSummary = data.summaryMilestones?.some(
          sm => sm.type === 'milestone' && (
            sm.id === milestone.id ||
            sm.id === `milestone-start-${milestone.id}` ||
            sm.id === `milestone-end-${milestone.id}`
          )
        );
        const milestoneHighlightStyle = '';
        const mHlStyle = data.settings.highlightStyle || HIGHLIGHT_BAR_STYLE_DEFAULTS;
        const mBarFill = isMilestoneInSummary ? hexToRgba(mHlStyle.fill_color, mHlStyle.fill_opacity) : milestoneColor;
        const mBarBorder = isMilestoneInSummary && mHlStyle.border_enabled
          ? `${mHlStyle.border_width}px dashed ${mHlStyle.border_color}`
          : 'none';
        
        const isHighlighted = milestone.isHighlighted;
        const barOpacity = isHighlighted ? 0.95 : 0.2;
        const barZIndex = isHighlighted ? 12 : 8;

        
        const projectCol = data.isMultiProject ? '<div class="w-[100px] p-2 text-xs border-r">&nbsp;</div>' : '';
        const categoryCol = data.settings.showCategories ? '<div class="w-[80px] p-2 text-xs border-r">&nbsp;</div>' : '';
        
        const bgCols = dateColumns.map(col => {
                      const totalDays = data?.timelineRange?.totalDays ?? (Math.floor((new Date(page.endDate).getTime() - new Date(page.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1);
                      const bgClass = col.isToday ? 'bg-primary/10' : col.isWeekend ? 'bg-muted/70' : '';
                      const isMonday = col.dayOfWeek === 1;
                      const borderClass = col.isMonthStart ? 'timeline-month-line' : 
                                         (totalDays > 150 && isMonday) ? 'timeline-week-line' :
                                         (totalDays <= 150 && col.isWeekend) ? 'timeline-weekend-line' : 
                                         (totalDays <= 150 ? 'timeline-grid-line' : '');
                      return `<div class="flex-shrink-0 ${bgClass} ${borderClass}" style="width: ${page.dayWidth}px; height: 100%; box-sizing: border-box;"></div>`;
                    }).join('');
                    
                    return `
                       <div class="flex border-b milestone-row items-center" style="${milestoneHighlightStyle}">
                         ${renderIndexCellHTML(sttLabel)}
                         ${getAvatarCellHTML(milestone.assigned_user_id, data.teamMembers, true)}
                         <div class="w-[240px] p-2 text-xs border-r text-muted-foreground" 
                               style="display: flex; align-items: center; padding-left: 40px; min-width: 240px; max-width: 240px;"
                               title="${milestone.name}${partialIndicator ? ' (partial)' : ''}">
                           ${getMilestoneStatusIconInline(milestone.status || 'pending')}
                           <span class="truncate">${partialIndicator}${milestone.name}</span>
                         </div>
                        <div class="w-[35px] p-2 text-[8px] border-r text-center text-muted-foreground flex items-center justify-center">${milestone.completion_percentage ?? 0}%</div>
                        ${projectCol}
                        ${categoryCol}
                        <div class="w-[35px] p-2 text-[8px] border-r text-center text-muted-foreground flex items-center justify-center">${durationDays}d</div>
                        <div class="w-[55px] min-w-[55px] p-1 text-[8px] border-r text-center text-muted-foreground whitespace-nowrap flex items-center justify-center">
                          ${format(new Date(milestone.start_date), 'dd-MM-yy')}
                        </div>
                        <div class="w-[55px] min-w-[55px] p-1 text-[8px] border-r text-center text-muted-foreground whitespace-nowrap flex items-center justify-center">
                          ${format(new Date(milestone.end_date || milestone.start_date), 'dd-MM-yy')}
                        </div>
                        <div class="flex-1 relative overflow-hidden" style="min-height: 24px;">
                          <div class="absolute inset-0 flex" style="z-index: 0;">
                            ${bgCols}
                          </div>
                          <!-- Milestone bar wrapper with completion -->
                          <div 
                            class="print-timeline-bar"
                            style="
                              left: ${(adjustedStartIndex * page.dayWidth).toFixed(2)}px;
                              width: ${(Math.max(page.dayWidth, milestoneDuration * page.dayWidth)).toFixed(2)}px;
                              z-index: ${barZIndex};
                              ${isMilestoneInSummary
                                ? (mHlStyle.border_enabled ? `outline: ${mBarBorder}; outline-offset: -${mHlStyle.border_width}px;` : '')
                                : (startsBeforePage ? 'border-left: 3px solid ' + milestoneColor + ';' : '') +
                                  (endsAfterPage ? 'border-right: 3px solid ' + milestoneColor + ';' : '')
                              }
                "
              >
                <!-- Base bar (mờ) -->
                <div style="
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  background-color: ${mBarFill};
                  opacity: ${isMilestoneInSummary ? 1 : 0.4};
                  border-radius: 4px;
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                "></div>
                
                <!-- Completion bar (đậm) -->
                ${!isMilestoneInSummary && milestone.completion_percentage > 0 ? `
                <div style="
                  position: absolute;
                  top: 0;
                  left: 0;
                  height: 100%;
                  width: ${milestone.completion_percentage}%;
                  background-color: ${milestoneColor};
                  opacity: ${isHighlighted ? 0.95 : 0.6};
                  border-radius: 4px;
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                "></div>
                ` : ''}
              </div>

            </div>
          </div>
        `;
      }
    }).filter(html => html.trim() !== '').join(''); // ✅ Filter empty HTML strings

    // First page has report header + table header, subsequent pages only have table header
    // Account for MilestoneSummary section if present (~6 rows equivalent)
    const isFirstPageOfReport = page === data.pages[0];
    const hasMilestoneSummary = data.summaryMilestones && data.summaryMilestones.length > 0;
    
    // Increased by +3 to better utilize page space (~85-90% capacity)
    const ROWS_FOR_FIRST_PAGE =
      isFirstPageOfReport && hasMilestoneSummary ? 15 :
      isFirstPageOfReport ? 21 : 25;
    const ROWS_FOR_SUBSEQUENT = 25;
    
    // Flatten items into rows and chunk by row count
    const flatRows = flattenItemsToRows(page.items);
    const rowChunks = chunkRowsForPrint(flatRows, ROWS_FOR_FIRST_PAGE, ROWS_FOR_SUBSEQUENT);

    // ✅ Debug logging
    console.log('[PDF Export Debug]', {
      pageNumber: page.pageNumber,
      itemsCount: page.items.length,
      flatRowsCount: flatRows.length,
      chunksCount: rowChunks.length,
      ROWS_FOR_FIRST_PAGE,
      ROWS_FOR_SUBSEQUENT,
      isFirstPageOfReport,
      chunksDetails: rowChunks.map((chunk, index) => ({
        index,
        length: chunk.length,
        types: chunk.map(row => row.type)
      }))
    });

    // PART B: compute all dependency edges for this page once (pure, mirrors on-screen logic)
    const pageEdges = computeDependencyEdges({
      items: page.items as any,
      milestonesByItem: (data.milestonesByItem as any) || {},
      itemDeps: data.itemDeps,
      msDeps: data.msDeps,
      dateColumns,
      dayWidth: page.dayWidth,
    });

    // Fixed (non-timeline) columns left of the timeline area
    // ✅ Bug fix: SVG offset must match bar row's fixed cells exactly.
    // Bar row renders BOTH index (28px) AND avatar (24px) cells before name.
    // Previously only 24 was counted → SVG drifted left 28px,
    // showing as N-day shift where N = 28 / dayWidth (worsens at longer ranges).
    const fixedColsWidth =
      28 + 24 + 240 + 35 +
      (data.isMultiProject ? 100 : 0) +
      (data.settings.showCategories ? 80 : 0) +
      35 + 55 + 55;

    // Helper: per-chunk row Y/height map.
    // Print CSS forces every `.timeline-rows > .flex.border-b` to a FIXED 24px
    // (height + max-height + overflow:hidden), so the on-screen "min-height: 32px"
    // for items-with-milestones does NOT actually grow the row in PDF.
    // → all rows (item, item-with-ms, milestone, continuation header) = 24px.
    const ROW_H = 24;
    const buildRowYMap = (chunk: FlatRow[], startsWithCont: boolean) => {
      const map = new Map<string, { y: number; h: number }>();
      let y = 0;
      if (startsWithCont) y += ROW_H;
      for (const row of chunk) {
        if (row.type === 'item') {
          map.set(`item-${row.item.id}`, { y, h: ROW_H });
        } else if (row.type === 'group') {
          y += ROW_H;
          continue;
        } else {
          map.set(`ms-${row.milestone.id}`, { y, h: ROW_H });
        }
        y += ROW_H;
      }
      return { map, totalHeight: y };
    };

    return rowChunks
      .filter(chunk => chunk.length > 0)
      .map((chunk, chunkIndex) => {
        const isFirstChunkOfReport = page === data.pages[0] && chunkIndex === 0;
        const firstRow = chunk[0];
        const startsWithMilestone = firstRow?.type === 'milestone';
        
        // Render rows for this chunk
        const renderedRows = renderFlatRows(chunk);
        
        // ✅ Skip empty chunks completely, with detailed debug
        if (!renderedRows.trim()) {
          console.warn('[PDF Export] Chunk has rows but rendered empty', {
            chunkIndex,
            chunkLength: chunk.length,
            chunkTypes: chunk.map(r => r.type)
          });
          return '';
        }

        // PART B: SVG overlay for dependency connectors (chunk-scoped; cross-chunk edges silently skipped)
        const { map: rowYMap, totalHeight } = buildRowYMap(chunk, startsWithMilestone);
        const chunkEdges = pageEdges.filter(e => rowYMap.has(e.anchorRowKey) && rowYMap.has(e.targetRowKey));
        if (exportStats) exportStats.edgesDrawn += chunkEdges.length;

        // Stagger labels per target row to avoid overlap (mirror on-screen)
        const orderIdx = new Map<string, number>();
        {
          const groups = new Map<number, typeof chunkEdges>();
          for (const ep of chunkEdges) {
            const ty = rowYMap.get(ep.targetRowKey)!;
            const rk = Math.round(ty.y + ty.h / 2);
            const arr = groups.get(rk) || [];
            arr.push(ep);
            groups.set(rk, arr);
          }
          for (const arr of groups.values()) {
            arr.sort((a, b) => a.targetX - b.targetX);
            arr.forEach((ep, i) => orderIdx.set(ep.key, i));
          }
        }

        const svgWidth = dateColumns.length * page.dayWidth;
        const svgInner = chunkEdges.map((ep) => {
          const ay = rowYMap.get(ep.anchorRowKey)!;
          const ty = rowYMap.get(ep.targetRowKey)!;
          const p1x = ep.anchorX;
          const p1y = ay.y + ay.h / 2;
          const p2x = ep.targetX;
          const p2y = ty.y + ty.h / 2;
          let d: string;
          if (p1x === p2x || p1y === p2y) {
            d = `M ${p1x} ${p1y} L ${p2x} ${p2y}`;
          } else {
            d = `M ${p1x} ${p1y} L ${p2x} ${p1y} L ${p2x} ${p2y}`;
          }
          if (ep.actualX != null && ep.actualX !== p2x) {
            d += ` L ${ep.actualX} ${p2y}`;
          }
          const label = ep.lag !== 0
            ? `${ep.type}${ep.lag > 0 ? '+' : ''}${ep.lag}d`
            : ep.type;
          const labelX = p2x + (p2x >= p1x ? 4 : -4);
          const idx = orderIdx.get(ep.key) ?? 0;
          const labelY = p2y - 4 - idx * 11;
          const anchorAttr = p2x >= p1x ? 'start' : 'end';
          const actualDot = (ep.actualX != null && ep.actualX !== p2x)
            ? `<circle cx="${ep.actualX}" cy="${p2y}" r="2" fill="#94a3b8"/>`
            : '';
          return `
            <g>
              <path d="${d}" fill="none" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="4 3"/>
              <circle cx="${p1x}" cy="${p1y}" r="2.5" fill="#94a3b8"/>
              <circle cx="${p2x}" cy="${p2y}" r="2.5" fill="#94a3b8"/>
              ${actualDot}
              <text x="${labelX}" y="${labelY}" font-size="9" fill="#94a3b8" text-anchor="${anchorAttr}" style="user-select:none;">${label}</text>
            </g>
          `;
        }).join('');

        const svgHtml = chunkEdges.length > 0 ? `
          <svg
            xmlns="http://www.w3.org/2000/svg"
            style="position:absolute; top:0; left:${fixedColsWidth}px; width:${svgWidth}px; height:${totalHeight}px; pointer-events:none; z-index:31; overflow:visible;"
            width="${svgWidth}"
            height="${totalHeight}"
          >${svgInner}</svg>
        ` : '';

        return `
          <div class="timeline-chunk" style="max-width: ${timelineContentWidth}px; margin: 0 auto;">
            ${renderTimelineHeader()}
            <div class="timeline-rows" style="position: relative;">
              ${startsWithMilestone ? renderContinuationHeader(firstRow.parentItem) : ''}
              ${renderedRows}
              ${svgHtml}
            </div>
          </div>
        `;
      })
      .filter(html => html.trim() !== '')  // ✅ Filter out empty chunks
      .join('');
  };


  return {
    exportTimelineReport,
    isExporting,
    buildTimelineHtmlDocument
  };
};